// Client-side list filtering shared by WnTableToolbar / WnFilterPanel.
//
// group: { id, type: 'daterange'|'checkbox'|'radio'|'select'|'text'|'number'|'numberrange', label, options?, presets?,
//          field?: string | (row) => value, match?: (row, value) => boolean }
// value: { [group.id]: daterange {from,to,preset} | checkbox string[] | radio/select/text string ('' = all)
//                      | number {op:'gt'|'gte'|'lt'|'lte'|'eq', value} | numberrange {min,max} }

const pad = (n) => String(n).padStart(2, '0');
export const toIso = (d) => `${ d.getFullYear() }-${ pad(d.getMonth() + 1) }-${ pad(d.getDate()) }`;

export function datePreset(key) {
    const t = new Date(), y = t.getFullYear(), m = t.getMonth(), d = t.getDate();
    const [from, to] = {
        today: [t, t],
        yesterday: [new Date(y, m, d - 1), new Date(y, m, d - 1)],
        month: [new Date(y, m, 1), new Date(y, m + 1, 0)],
        lastMonth: [new Date(y, m - 1, 1), new Date(y, m, 0)],
        year: [new Date(y, 0, 1), new Date(y, 11, 31)],
    }[key];
    return { preset: key, from: toIso(from), to: toIso(to) };
}

const read = (group, row) => typeof group.field === 'function' ? group.field(row) : row[group.field ?? group.id];

const filled = (n) => n !== undefined && n !== null && n !== '';

export function isGroupActive(group, v) {
    if (group.type === 'daterange') return !!(v?.from || v?.to);
    if (group.type === 'checkbox') return !!v?.length;
    if (group.type === 'number') return filled(v?.value);
    if (group.type === 'numberrange') return filled(v?.min) || filled(v?.max);
    return !!v;
}

export const countActive = (groups, value) => groups.filter((g) => isGroupActive(g, value?.[g.id])).length;

export function groupMatches(group, row, v) {
    if (group.match) return !!group.match(row, v);

    const field = read(group, row);

    if (group.type === 'daterange') {
        const day = field ? toIso(new Date(field)) : null;
        return !!day && (!v.from || day >= v.from) && (!v.to || day <= v.to);
    }

    if (group.type === 'checkbox') {
        return [].concat(field ?? []).some((f) => v.includes(String(f)));
    }

    if (group.type === 'text') {
        return String(field ?? '').toLowerCase().includes(String(v).trim().toLowerCase());
    }

    if (group.type === 'number' || group.type === 'numberrange') {
        const n = Number(field);

        if (field === null || field === undefined || field === '' || !Number.isFinite(n)) return false;
        if (group.type === 'numberrange') return (!filled(v.min) || n >= Number(v.min)) && (!filled(v.max) || n <= Number(v.max));

        const t = Number(v.value);

        return { gt: n > t, gte: n >= t, lt: n < t, lte: n <= t, eq: n === t }[v.op || 'gt'];
    }

    return String(field ?? '') === v;
}

export function applyFilters(rows, groups, value) {
    const active = groups.filter((g) => isGroupActive(g, value?.[g.id]));
    return active.length ? rows.filter((row) => active.every((g) => groupMatches(g, row, value[g.id]))) : rows;
}

// Every whitespace separated term must be found in at least one of the texts (AND, case-insensitive).
export function matchesSearch(texts, query) {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return true;
    const haystack = texts.filter((t) => t !== null && t !== undefined).join('\n').toLowerCase();
    return terms.every((t) => haystack.includes(t));
}

// Bridges WnFilterPanel's group defs (id/type/match, value keyed by plain group.id) to
// WiseCommon.mixinsFactory()'s field-filtering engine (settings keyed by searchArgs key), so a list
// view's filters run through the shared mixin instead of a bespoke filteredRows computed.
//
// Mixin settings keys are prefixed ('f:<id>') rather than using the bare group.id, because
// mixinsFactory's loop does `if (row[key] === null) return false` BEFORE calling a custom filter -
// if group.id matches a real (nullable) row property, that blanket check can reject a row a group's
// own match() would have accepted (e.g. a radio filter whose "no value" option matches row.x === null).
// Prefixing means that guard only ever sees `row['f:<id>']`, which is always undefined, never null.
const mixinKey = (id) => `f:${ id }`;

export function filterMixinSettings(groups, searchFields) {
    const settings = {};

    for (const g of groups) {
        settings[mixinKey(g.id)] = (search, row) => !isGroupActive(g, search) || groupMatches(g, row, search);
    }

    if (searchFields) {
        settings.q = (search, row) => !search.trim() || matchesSearch(searchFields(row), search);
    }

    return settings;
}

// searchArgs (mixin state, 'f:<id>'-keyed) <-> filterValue (WnFilterPanel's value/apply, plain id-keyed)
export function toFilterValue(groups, searchArgs) {
    const value = {};
    for (const g of groups) value[g.id] = searchArgs[mixinKey(g.id)];
    return value;
}

export function applyFilterValue(groups, searchArgs, value) {
    for (const g of groups) searchArgs[mixinKey(g.id)] = value[g.id] ?? '';
}
